import React, { useState, useEffect } from 'react';
import { mobileAccessKeysAPI } from '../lib/api';
import toast from 'react-hot-toast';

const MobileAccessKeys = () => {
  const [accessKeys, setAccessKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyDescription, setNewKeyDescription] = useState('');
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadAccessKeys();
  }, []);

  const loadAccessKeys = async () => {
    try {
      setLoading(true);
      const data = await mobileAccessKeysAPI.getAll();
      setAccessKeys(data || []);
    } catch (error) {
      console.error('Error loading access keys:', error);
      const errorMessage = error.message || 'Bilinmeyen hata';
      toast.error(`Erişim anahtarları yüklenemedi: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const generateAccessKey = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = 'dcak_'; // Domain Control Access Key prefix
    for (let i = 0; i < 32; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Lütfen bir anahtar adı girin');
      return;
    }

    try {
      const newKey = generateAccessKey();
      await mobileAccessKeysAPI.create({
        name: newKeyName,
        description: newKeyDescription,
        key: newKey,
        active: true,
      });

      setCreatedKey(newKey);
      setNewKeyName('');
      setNewKeyDescription('');
      setShowCreateModal(false);
      loadAccessKeys();
      toast.success('Erişim anahtarı başarıyla oluşturuldu');
    } catch (error) {
      console.error('Error creating access key:', error);
      const errorMessage = error.message || 'Bilinmeyen hata';
      toast.error(`Erişim anahtarı oluşturulamadı: ${errorMessage}`);
    }
  };

  const handleToggleActive = async (keyId, currentStatus) => {
    try {
      await mobileAccessKeysAPI.update(keyId, { active: !currentStatus });
      loadAccessKeys();
      toast.success('Erişim anahtarı durumu güncellendi');
    } catch (error) {
      console.error('Error updating access key:', error);
      const errorMessage = error.message || 'Bilinmeyen hata';
      toast.error(`Erişim anahtarı güncellenemedi: ${errorMessage}`);
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm('Bu erişim anahtarını silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      await mobileAccessKeysAPI.delete(keyId);
      loadAccessKeys();
      toast.success('Erişim anahtarı başarıyla silindi');
    } catch (error) {
      console.error('Error deleting access key:', error);
      const errorMessage = error.message || 'Bilinmeyen hata';
      toast.error(`Erişim anahtarı silinemedi: ${errorMessage}`);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Anahtar panoya kopyalandı');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('tr-TR');
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Mobile Access Keys
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Mobil uygulama yapılandırması için erişim anahtarlarını yönetin
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Yeni Anahtar Oluştur
        </button>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <svg className="w-6 h-6 text-blue-600 dark:text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="flex-1">
            <h3 className="font-semibold text-blue-900 dark:text-blue-100">Nasıl Kullanılır</h3>
            <p className="text-sm text-blue-800 dark:text-blue-200 mt-1">
              Erişim anahtarını mobil uygulama kullanıcılarıyla paylaşın. Kullanıcılar bu anahtarı sunucu URL'si ile birlikte mobil uygulamaya girecekler.
            </p>
          </div>
        </div>
      </div>

      {/* Access Keys List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Yükleniyor...</p>
        </div>
      ) : accessKeys.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <p className="text-gray-600 dark:text-gray-400">Henüz erişim anahtarı yok</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            İlk Anahtarı Oluştur
          </button>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Ad
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Açıklama
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Anahtar
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Durum
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Oluşturulma
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Son Kullanım
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {accessKeys.map((key) => (
                <tr key={key.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">{key.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500 dark:text-gray-400">{key.description || '-'}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded font-mono">
                        {key.key.substring(0, 20)}...
                      </code>
                      <button
                        onClick={() => copyToClipboard(key.key)}
                        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                        title="Kopyala"
                      >
                        📋
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      key.active 
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                        : 'bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-300'
                    }`}>
                      {key.active ? 'Aktif' : 'Pasif'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(key.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {key.last_used ? formatDate(key.last_used) : 'Hiç kullanılmadı'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleActive(key.id, key.active)}
                        className={`px-3 py-1 rounded text-xs ${
                          key.active
                            ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-900/50'
                            : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50'
                        }`}
                      >
                        {key.active ? 'Pasif Yap' : 'Aktif Yap'}
                      </button>
                      <button
                        onClick={() => handleDeleteKey(key.id)}
                        className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 rounded text-xs hover:bg-red-200 dark:hover:bg-red-900/50"
                      >
                        Sil
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Yeni Erişim Anahtarı Oluştur
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Anahtar Adı *
                </label>
                <input
                  type="text"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Örn: Mobil Uygulama Anahtarı"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Açıklama
                </label>
                <textarea
                  value={newKeyDescription}
                  onChange={(e) => setNewKeyDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Anahtarın kullanım amacını açıklayın"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewKeyName('');
                  setNewKeyDescription('');
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                İptal
              </button>
              <button
                onClick={handleCreateKey}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Oluştur
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Created Key Modal */}
      {createdKey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Erişim Anahtarı Oluşturuldu
            </h2>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
              <p className="text-sm text-yellow-800 dark:text-yellow-200 mb-2">
                <strong>Önemli:</strong> Bu anahtarı güvenli bir yerde saklayın. Bir daha gösterilmeyecek!
              </p>
            </div>
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mb-4">
              <code className="text-sm font-mono break-all text-gray-900 dark:text-white">
                {createdKey}
              </code>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => copyToClipboard(createdKey)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {copied ? 'Kopyalandı!' : 'Anahtarı Kopyala'}
              </button>
              <button
                onClick={() => {
                  setCreatedKey(null);
                  setShowCreateModal(false);
                }}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileAccessKeys;

